import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import GoogleButton from "@/components/GoogleButton";
import { ArrowUpRight, Building2, Rocket, KeyRound } from "lucide-react";

const TYPES = [
  { id: "entrepreneur", label: "Entrepreneur", icon: Rocket, desc: "$195/year — Full platform access." , testId: AUTH.signupTypeEntrepreneur },
  { id: "organization", label: "Organization", icon: Building2, desc: "Create a workspace for your program.", testId: AUTH.signupTypeOrganization },
  { id: "invite", label: "Invitation Code", icon: KeyRound, desc: "Join an organization with a code.", testId: AUTH.signupTypeInvite },
];

export default function Signup() {
  const [searchParams] = useSearchParams();
  const initialCode = (searchParams.get("code") || searchParams.get("invitation_code") || "").toUpperCase();
  const [type, setType] = useState(initialCode ? "invite" : "entrepreneur");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", organization_name: "", invitation_code: initialCode });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialCode) {
      toast.success(`Access code ${initialCode} applied. Complete signup to unlock free access.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { full_name: form.full_name, email: form.email, password: form.password };
    if (type === "organization") payload.organization_name = form.organization_name;
    if (type === "invite") payload.invitation_code = form.invitation_code;
    try {
      const u = await signup(payload);
      toast.success(`Welcome to ATLAS™, ${u.full_name.split(" ")[0]}!`);
      // Take entrepreneurs directly into the Business DNA™ onboarding.
      navigate(u.role === "entrepreneur" ? "/app/onboarding" : "/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between border-r border-slate-800 bg-slate-950 p-12 text-white">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-950">
            <span className="font-display text-sm font-bold">A</span>
          </div>
          <div>
            <div className="font-display text-lg font-bold">ATLAS™</div>
            <div className="label-mono !text-[9px] !text-stone-500">The IXL Business Pro</div>
          </div>
        </Link>
        <div>
          <h2 className="font-display text-5xl font-bold leading-tight tracking-tight">
            Build stronger businesses.<br />
            <span className="text-stone-400">Measure real impact.</span>
          </h2>
          <p className="mt-6 max-w-md text-base text-stone-300">
            Join thousands of entrepreneurs and organizations transforming programs into measurable economic outcomes.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
          Powered by AIH LLC
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-md">
          <div className="label-mono mb-3">Create Account</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Get started with ATLAS™.</h1>

          <div className="mt-6 grid grid-cols-3 gap-2">
            {TYPES.map(({ id, label, icon: Icon, testId }) => (
              <button
                key={id}
                type="button"
                onClick={() => setType(id)}
                data-testid={testId}
                className={`flex flex-col items-start gap-2 rounded-md border p-3 text-left transition-all ${type === id ? "border-slate-950 bg-white shadow-sm" : "border-stone-200 bg-stone-50 hover:border-stone-400"}`}
              >
                <Icon className="h-4 w-4 text-slate-950" />
                <div className="text-xs font-medium text-slate-950">{label}</div>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-stone-500">{TYPES.find((t) => t.id === type)?.desc}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="label-mono block mb-1.5">Full Name</span>
              <input data-testid={AUTH.signupName} required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
            </label>
            <label className="block">
              <span className="label-mono block mb-1.5">Email</span>
              <input data-testid={AUTH.signupEmail} required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} autoComplete="email" />
            </label>
            <label className="block">
              <span className="label-mono block mb-1.5">Password</span>
              <input data-testid={AUTH.signupPassword} required minLength={8} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} autoComplete="new-password" />
            </label>
            {type === "organization" && (
              <label className="block">
                <span className="label-mono block mb-1.5">Organization Name</span>
                <input data-testid={AUTH.signupOrgName} required value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} className={inputCls} placeholder="e.g. Austin Chamber of Commerce" />
              </label>
            )}
            {type === "invite" && (
              <label className="block">
                <span className="label-mono block mb-1.5">Invitation Code</span>
                <input data-testid={AUTH.signupInvite} required value={form.invitation_code} onChange={(e) => setForm({ ...form, invitation_code: e.target.value.toUpperCase() })} className={`${inputCls} font-mono uppercase`} placeholder="ABC123XYZ" />
              </label>
            )}
            <button data-testid={AUTH.signupSubmit} disabled={loading} type="submit" className="btn-primary w-full">
              {loading ? "Creating…" : (<>Create Account <ArrowUpRight className="h-4 w-4" /></>)}
            </button>
          </form>
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-stone-400">or</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>
          <GoogleButton label="Sign up with Google" testId="google-signup-btn" />
          <div className="mt-8 border-t border-stone-200 pt-6 text-sm text-stone-600">
            Already have an account?{" "}
            <Link data-testid={AUTH.signupToLogin} to="/login" className="font-medium text-slate-950 hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
